/**
 * Парсирање броеви во OS/RN табелите.
 *
 * Постојат ДВА различни извора на бројчани стрингови и тие НЕ смеат да се парсираат исто:
 *
 *  1. Тоа што го КУЦА корисникот. На македонска тастатура децималниот знак е запирка —
 *     „1,5" значи еден и пол. Дотогаш секоја од овие табели го решаваше ова поинаку:
 *     Размерување бришеше запирки (→ 15), Меѓу/Полупроизвод ја претворaше во точка (→ 1.5),
 *     а save-патот викаше гол `Number("1,5")` → **NaN**, што `JSON.stringify` го праќа како
 *     `null`. Количината се губеше тивко, без ниту една грешка. За рецептура тоа е критичен
 *     податок (EU GMP Ch. 4 §4.14), затоа сега сите поминуваат низ `parseDecimalInput`.
 *
 *  2. Тоа што го произведува `toLocaleString("en-US")` — таму запирката е групирање на
 *     илјади („1,234.20"), а точката е децимална. Тие стрингови одат низ
 *     `parseFormattedNumber`, никогаш низ `parseDecimalInput`.
 *
 * Мешањето на двете е причината за багот, па двете функции се одделни намерно.
 *
 * `formatQtyForDisplay` е приказниот дел од истиот пар со `parseDecimalInput` — она што
 * `parseDecimalInput` го чита, `formatQtyForDisplay` го пишува, за КОЛИЧИНИ. И таа никогаш
 * не групира илјади, од истата причина како погоре.
 *
 * Парите и единечните цени имаат свои форматери (`formatMoney`, `formatUnitPrice`).
 * Поле во уредување оди низ `formatForEdit` (без групирање). Ниту еден од нив не се
 * потпира на локалот `mk-MK`: сепараторите се пишуваат експлицитно, за резултатот да
 * е ист во секој прелистувач. Македонски прикажан стринг (`1.234,56`) се чита назад
 * со `parseDecimalInput`, никогаш со `parseFormattedNumber`.
 */

/**
 * Број како што го напишал ЧОВЕК. Прифаќа „1,5", „1.5", „1 234,5", „1.234,5", „1,234.5".
 *
 * Правило: децималниот сепаратор е оној од `,`/`.` што се појавува ПОСЛЕДЕН; сите
 * појавувања на другиот се групирање и се бришат. Празно/невалидно → `null`.
 *
 * Свесна двосмисленост: „1,234" (само запирка, три цифри по неа) се чита како **1.234**,
 * не како 1234 — бидејќи ова е внес од корисник, а во МК конвенција запирката е децимална.
 * Затоа машински форматираните вредности мора да одат низ `parseFormattedNumber`.
 */
export const parseDecimalInput = (value: unknown): number | null => {
  if (value === null || value === undefined) return null;
  if (typeof value === "number") return Number.isFinite(value) ? value : null;

  const cleaned = String(value).trim().replace(/\s/g, "");
  if (cleaned === "") return null;

  const lastComma = cleaned.lastIndexOf(",");
  const lastDot = cleaned.lastIndexOf(".");

  let normalized: string;
  if (lastComma === -1 && lastDot === -1) {
    normalized = cleaned;
  } else {
    const decSep = lastComma > lastDot ? "," : ".";
    const groupSep = decSep === "," ? "." : ",";
    const withoutGroups = cleaned.split(groupSep).join("");
    const parts = withoutGroups.split(decSep);
    normalized =
      parts.length > 1
        ? `${parts.slice(0, -1).join("")}.${parts[parts.length - 1]}`
        : withoutGroups;
  }

  const n = Number(normalized);
  return Number.isFinite(n) ? n : null;
};

/**
 * Број што го произвела `toLocaleString("en-US")` — запирките се групирање на илјади.
 * Користи ја САМО врз излез од сопствените форматери (`allowedRangeOf`, `allowedYieldOf`),
 * никогаш врз внес од корисник.
 */
export const parseFormattedNumber = (value: unknown): number | null => {
  if (value === null || value === undefined) return null;
  const cleaned = String(value).trim().replace(/\s/g, "").replace(/,/g, "");
  if (cleaned === "") return null;
  const n = Number(cleaned);
  return Number.isFinite(n) ? n : null;
};

/**
 * Минимални децимали за КОЛИЧИНА. `0` → `7,8`. Ако бизнисот одлучи `7,80`, смени ја
 * само оваа константа на `2`.
 */
export const QTY_MIN_FRACTION_DIGITS = 0;

const QTY_MAX_FRACTION_DIGITS = 6;
const MONEY_FRACTION_DIGITS = 2;
/** Децимали за единечна цена: поставка во Подесувања на компанија (2, 3 или 4; стандардно 4). */
let unitPriceFractionDigits = 4;

export const setUnitPriceFractionDigits = (n: number): void => {
  if (n === 2 || n === 3 || n === 4) unitPriceFractionDigits = n;
};

export const getUnitPriceFractionDigits = (): number => unitPriceFractionDigits;

/** Excel формат за единечна цена, според тековната поставка: `#,##0.0000`. */
export const unitPriceExcelFormat = (): string => "#,##0." + "0".repeat(unitPriceFractionDigits);
/**
 * Поле за уредување сече на 6. Во базата износите носат float-остаток над 6
 * децимали; ако се прикаже во полето, повторното снимање ја менува вредноста.
 * Приказот на вкупен износ и понатаму е точно 2 (`formatMoney`).
 */
const EDIT_MAX_FRACTION_DIGITS = 6;

/**
 * Стабилна децимална точка преку `en-US` без групирање, па експлицитна замена:
 * точката станува запирка, а илјадарките (ако се бараат) се точки.
 * Не користи `mk-MK` — ICU на прелистувачот не е ист насекаде.
 */
const formatExplicit = (
  n: number,
  minFractionDigits: number,
  maxFractionDigits: number,
  grouping: boolean,
): string => {
  const negative = n < 0;
  const plain = Math.abs(n).toLocaleString("en-US", {
    useGrouping: false,
    minimumFractionDigits: minFractionDigits,
    maximumFractionDigits: maxFractionDigits,
  });
  const [intRaw, frac] = plain.split(".");
  const intPart = grouping ? intRaw.replace(/\B(?=(\d{3})+(?!\d))/g, ".") : intRaw;
  const body = frac !== undefined && frac.length > 0 ? `${intPart},${frac}` : intPart;
  const roundedToZero = /^0(,0+)?$/.test(body);
  return negative && !roundedToZero ? `-${body}` : body;
};

/**
 * Приказ на КОЛИЧИНА — тоа е спротивната насока на `parseDecimalInput`, истиот пар.
 * На македонска тастатура децималниот знак е запирка, па количините се прикажуваат со
 * запирка („2,5"), никогаш со точка. НАМЕРНО без групирање на илјади
 * — групирачка запирка би се прочитала назад како ДЕЦИМАЛНА низ `parseDecimalInput` и
 * количината тивко би паднала (истиот баг опишан погоре). `0`/null/NaN/Infinity → празен
 * стринг, исто како постојното однесување на `formatQty` во OSRazmerTable (нема "0" во
 * празни количински полиња). Збир или колона во извештај каде нулата мора да се гледа
 * ја вика со `{ showZero: true }` → `0`. `fractionDigits` ја фиксира должината
 * (парчиња `0`, волумен `4`). Само за КОЛИЧИНИ.
 */
export const formatQtyForDisplay = (
  n: number | null | undefined,
  options?: { showZero?: boolean; fractionDigits?: number },
): string => {
  const min = options?.fractionDigits ?? QTY_MIN_FRACTION_DIGITS;
  const max = options?.fractionDigits ?? QTY_MAX_FRACTION_DIGITS;
  if (n === null || n === undefined || !Number.isFinite(n)) return "";
  if (n === 0) {
    if (!options?.showZero) return "";
    return formatExplicit(0, min, max, false);
  }
  return formatExplicit(n, min, max, false);
};

/**
 * Вкупен паричен износ (вредност на ред, збир, ДДВ износ, за плаќање).
 * `1.234.567,89` — точка за илјадарки, запирка децимална, секогаш точно 2 децимали.
 * `0` → `0,00`. null/NaN → `""`.
 */
export const formatMoney = (n: number | null | undefined): string => {
  if (n === null || n === undefined || !Number.isFinite(n)) return "";
  return formatExplicit(n, MONEY_FRACTION_DIGITS, MONEY_FRACTION_DIGITS, true);
};

/**
 * Единечна цена (набавна, продажна, вреднувачка, цена по единица).
 * Иста форма како парите, но точно толку децимали колку што е поставено во Подесувања
 * на компанија (2, 3 или 4; стандардно 4, како PANTHEON): `12,5000` · `0,0345`.
 * `0` → `0,0000`. null/NaN → `""`.
 */
export const formatUnitPrice = (n: number | null | undefined): string => {
  if (n === null || n === undefined || !Number.isFinite(n)) return "";
  return formatExplicit(n, unitPriceFractionDigits, unitPriceFractionDigits, true);
};

/**
 * Девизен курс (курсна листа). Точно 4 децимали со групирање:
 * `61,5337` · `0,0345` · `1.234,5678`. НБРМ листата ги прикажува курсевите
 * со 4 децимали (исто како `formatUnitPrice`). null/NaN → `""`.
 */
export const formatExchangeRate = (n: number | null | undefined): string => {
  if (n === null || n === undefined || !Number.isFinite(n)) return "";
  return formatExplicit(n, 4, 4, true);
};

/**
 * Поле во уредување: без сепаратор за илјадарки, запирка, најмногу 6 децимали.
 * `1234567,891` · `7000` · `0`. null/NaN → `""`.
 * Групирањето (`formatMoney` / `formatUnitPrice`) е само за ќелии што не се уредуваат.
 */
export const formatForEdit = (n: number | null | undefined): string => {
  if (n === null || n === undefined || !Number.isFinite(n)) return "";
  return formatExplicit(n, 0, EDIT_MAX_FRACTION_DIGITS, false);
};
